<template>
  <div class="configContactInfoVue">
    <div class="col-md-10 col-sm-9 u-pad-all-25 c-common-box-shadow u-bg-color-fff">
      <h3 class="u-cmn-botmargin">Contact info</h3>
      <p class="o-blue-regular-text">
        Who to reach for this local. These are <strong>contact records</strong> — they do not
        create logins and give no access to CallSteward. The same email may be used for more
        than one position.
      </p>

      <!-- #1761 (ed) — the checkbox read as a bare permission: whoever fills this
           in could not tell what they were agreeing to or who would be calling.
           Stated ONCE at the top rather than per row.
           Worded as what ticking PERMITS, not what the system does: per #1737 the
           flag is captured only and nothing routes a call on it yet, so "may
           forward" is the honest tense. -->
      <p class="cci__forward-note" data-testid="cci-forward-note">
        <strong>Call forwarding.</strong>
        These boxes record permission for <strong>calls forwarded from CallSteward</strong>.
        Tick the box beside a contact if CallSteward <em>may</em> forward incoming calls to
        that number; leave it unticked and it may not. This records permission only — call
        forwarding is not switched on yet.
      </p>

      <p v-if="notice" class="cci__notice" data-testid="cci-notice">
        <i class="fa fa-info-circle" aria-hidden="true"></i> {{ notice }}
      </p>
      <p v-if="error" class="cci__error" data-testid="cci-error">
        <i class="fa fa-exclamation-triangle" aria-hidden="true"></i> {{ error }}
      </p>

      <div v-if="loading" class="cci__loading" data-testid="cci-loading">
        <i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Loading contact info…
      </div>

      <table v-else class="table cci__table" data-testid="cci-table">
        <thead>
          <tr>
            <th class="cci__col-pos">Position</th>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th class="cci__col-fwd" title="Permission only — CallSteward does not forward calls yet">
              May forward calls
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in contacts" :key="row.position_id" :data-testid="'cci-row'">
            <td class="cci__pos">{{ row.position }}</td>
            <td>
              <input type="text" class="form-control" v-model="row.name"
                     :placeholder="row.position + ' name'"
                     :data-testid="'cci-name-' + slug(row.position)" />
            </td>
            <td>
              <input type="email" class="form-control" v-model="row.email"
                     placeholder="email"
                     :data-testid="'cci-email-' + slug(row.position)" />
            </td>
            <td>
              <input type="text" class="form-control" v-model="row.phone"
                     placeholder="phone"
                     :data-testid="'cci-phone-' + slug(row.position)" />
            </td>
            <td class="cci__fwd">
              <input type="checkbox" v-model="row.forward_calls"
                     :data-testid="'cci-forward-' + slug(row.position)" />
            </td>
          </tr>
        </tbody>
      </table>

      <div class="cci__actions" v-if="!loading">
        <button class="c-common-btn" type="button" :disabled="saving"
                @click="save" data-testid="cci-save">
          <i class="fa fa-save" aria-hidden="true"></i>
          {{ saving ? 'Saving…' : 'Save contact info' }}
        </button>
        <span class="cci__hint">
          Clearing a row's name, email and phone removes that contact.
        </span>
      </div>
    </div>
  </div>
</template>

<script>
// #1737 — Config → Contact info. DATA ONLY: capture per-client officer contacts
// and an OK-to-forward-calls permission. No CRM sync and no call forwarding in
// this pass; the flag is stored and nothing acts on it.
//
// These are NOT users. Nothing here touches the users table or any login path,
// and duplicate emails are allowed on purpose (cf #1677/#1679).
import service from "../../services.js";
import { mapGetters } from "vuex";

export default {
  name: "ConfigContactInfo",
  props: ["empDetails"],
  data() {
    return {
      contacts: [],
      loading: true,
      saving: false,
      notice: "",
      error: "",
    };
  },
  computed: {
    ...mapGetters({
      userToken: "userToken",
      userRole: "userRole",
      userClientID: "userClientID",
    }),
  },
  created() {
    this.load();
  },
  methods: {
    headers() {
      return {
        Authorization: this.userToken,
        "cs3-context-role": this.userRole,
        "cs3-context-clientId": this.userClientID,
      };
    },
    // Stable per-position test/selector suffix — "Vice President" -> "vice-president".
    slug(name) {
      return String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    },
    apiError(e, fallback) {
      const d = e && e.response ? e.response.data : null;
      const msg = d && (d.status_message || d.message);
      return typeof msg === "string" && msg ? msg : fallback;
    },
    load() {
      this.loading = true;
      this.error = "";
      service
        .get("client-contact", this.headers())
        .then((res) => {
          const r = (res && res.data && res.data.result) || {};
          // The server returns every position, populated or not, so the form
          // always renders all four rows in order even before a first save.
          this.contacts = Array.isArray(r.contacts) ? r.contacts : [];
        })
        .catch((e) => {
          this.contacts = [];
          this.error = this.apiError(e, "Could not load the contact info.");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    save() {
      this.saving = true;
      this.error = "";
      this.notice = "";
      const payload = {
        contacts: this.contacts.map((r) => ({
          position_id: r.position_id,
          name: r.name,
          email: r.email,
          phone: r.phone,
          forward_calls: !!r.forward_calls,
        })),
      };
      service
        .post("client-contact", payload, this.headers())
        .then(() => {
          this.notice = "Contact info saved.";
          // Re-read rather than trusting local state: a cleared row is removed
          // server-side, and the reload is what proves the save round-tripped.
          this.load();
        })
        .catch((e) => {
          this.error = this.apiError(e, "Could not save the contact info.");
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.cci__table { width: 100%; margin-top: 12px; }
.cci__table th { font-size: 12px; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; }
.cci__col-pos { width: 150px; }
.cci__col-fwd { width: 130px; text-align: center; }
.cci__pos { font-weight: 600; vertical-align: middle; }
.cci__fwd { text-align: center; vertical-align: middle; }
.cci__actions { margin-top: 14px; display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.cci__hint { color: #6b7280; font-size: 12.5px; }
.cci__loading { padding: 20px; color: #6b7280; }
.cci__forward-note {
  font-size: 13px; color: #334155; background: #eef4fb; border: 1px solid #cddef2;
  border-radius: 6px; padding: 10px 13px; margin: 0 0 12px; max-width: 760px;
}
.cci__notice {
  font-size: 13px; color: #1e5c2f; background: #e8f5ec; border: 1px solid #bfe0c8;
  border-radius: 6px; padding: 8px 11px; display: inline-block;
}
.cci__error {
  font-size: 13px; color: #9b1c1c; background: #fbe2e2; border: 1px solid #f0b8b8;
  border-radius: 6px; padding: 8px 11px; display: inline-block;
}
</style>
